import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DoNotDisturbAltRoundedIcon from "@mui/icons-material/DoNotDisturbAltRounded";

// "1" no va aca: en App Conductor es una cantidad (1 conductor) y tiene que verse el numero.
const POSITIVE_VALUES = new Set(["si", "yes", "true"]);
const NEGATIVE_VALUES = new Set(["no", "false", "0", "-", "n/a", "na"]);
const POSITIVE_TEXT_BADGES = new Set(["varias cuentas", "disponible"]);

const formatPrice = (value) => {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const normalizeFeatureValue = (value) => String(value || "").trim().toLowerCase();

const renderFeatureValue = (value) => {
  const normalized = normalizeFeatureValue(value);

  if (POSITIVE_VALUES.has(normalized)) {
    return <CheckCircleRoundedIcon fontSize="small" color="success" />;
  }

  if (POSITIVE_TEXT_BADGES.has(normalized)) {
    return <CheckCircleRoundedIcon fontSize="small" color="success" />;
  }

  if (NEGATIVE_VALUES.has(normalized)) {
    return <DoNotDisturbAltRoundedIcon fontSize="small" color="disabled" />;
  }

  return (
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value || "-"}
    </Typography>
  );
};

// Porcentaje que se ahorra al mes pagando anual. Sale de los precios del plan y no de
// pricing.discount_rate, que antes venia fijo en 35% y no calzaba con ningun plan.
export const annualSavingsPercent = (plan) => {
  const monthly = Number(plan?.pricing?.monthly);
  const annual = Number(plan?.pricing?.annual);
  if (plan?.type !== "paid" || !(monthly > 0) || !(annual > 0) || annual >= monthly) return 0;
  return Math.round((1 - annual / monthly) * 100);
};

const resolvePrice = (plan, billingCycle) => {
  const pricing = plan?.pricing || {};
  if (plan?.type !== "paid") {
    return {
      value: null,
      previousValue: null,
      currency: pricing.currency || "UF",
      savingsPercent: 0,
    };
  }

  const isAnnual = billingCycle === "annual";
  const savingsPercent = annualSavingsPercent(plan);

  return {
    value: isAnnual ? pricing.annual : pricing.monthly,
    previousValue: isAnnual && savingsPercent > 0 ? pricing.monthly : null,
    annualValue: pricing.annual,
    currency: pricing.currency || "UF",
    savingsPercent,
  };
};

export default function SubscriptionCard({
  plan,
  billingCycle,
  isCurrent,
  isSubmitting,
  onSelect,
  actionLabel,
}) {
  const highlighted = Boolean(plan?.recommended);
  const { value, previousValue, annualValue, currency, savingsPercent } = resolvePrice(plan, billingCycle);

  return (
    <Card
      variant={highlighted ? "elevation" : "outlined"}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        borderColor: "divider",
        boxShadow: highlighted ? 8 : 2,
        transition: "transform .2s ease, box-shadow .2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 8 },
      }}
      role="region"
      aria-label={`Plan ${plan?.name}`}
    >
      <Box sx={{ height: 10, background: plan?.color || "primary.main" }} />

      <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {plan?.name}
          </Typography>
          {highlighted && <Chip label="Recomendado" color="primary" size="small" sx={{ fontWeight: 700 }} />}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 42 }}>
          {plan?.description}
        </Typography>

        <Box mb={2} mt={2} textAlign="center" sx={{ minHeight: 82 }}>
          {plan?.type === "paid" && value !== null ? (
            <>
              <Typography variant="h4" sx={{ color: plan?.color || "primary.main", fontWeight: 700, mb: plan?.slug === "pyme" ? 0 : undefined }}>
                {formatPrice(value)} {currency}/mes
              </Typography>
              {/* En PYME el precio se cobra por cada conductor. */}
              {plan?.slug === "pyme" && (
                <Typography color="text.secondary" sx={{ display: "block", fontSize: "0.65rem", lineHeight: 1.2, mb: 0.5 }}>
                  x conductor
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {billingCycle === "annual" ? "Facturado anual" : "Facturado mensual"}
              </Typography>
              {previousValue !== null && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    Mensual:{" "}
                    <Box component="span" sx={{ textDecoration: "line-through" }}>
                      {formatPrice(previousValue)} {currency}/mes
                    </Box>
                  </Typography>
                  <Chip
                    label={`Ahorras ${savingsPercent}%`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              )}
              {billingCycle !== "annual" && savingsPercent > 0 && (
                <Typography variant="body2" color="success.main" sx={{ display: "block", mt: 0.5, fontWeight: 600 }}>
                  Con plan anual: {formatPrice(annualValue)} {currency}/mes (ahorras {savingsPercent}%)
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body1" fontWeight={600}>
              {plan?.cta_text}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <List
          dense
          disablePadding
          sx={{
            mt: 1,
            "& .feature-row": {
              display: "grid",
              gridTemplateColumns: "1fr auto",
              alignItems: "center",
              columnGap: 2,
              minHeight: 38,
              py: 0.5,
            },
          }}
        >
          {(plan?.feature_rows || []).map((row) => (
            <ListItem key={row.key} className="feature-row" disableGutters>
              <Typography variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {renderFeatureValue(row.value)}
              </Box>
            </ListItem>
          ))}
        </List>

        <Box mt="auto">
          <Button
            fullWidth
            variant={highlighted ? "contained" : "outlined"}
            size="medium"
            disabled={Boolean(isCurrent || isSubmitting)}
            sx={{ mt: 2, fontWeight: 600 }}
            onClick={() => onSelect?.(plan)}
          >
            {isCurrent ? "Plan actual" : actionLabel || plan?.cta_text || "Seleccionar plan"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

SubscriptionCard.propTypes = {
  plan: PropTypes.shape({
    slug: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    color: PropTypes.string,
    recommended: PropTypes.bool,
    cta_text: PropTypes.string,
    pricing: PropTypes.object,
    feature_rows: PropTypes.array,
  }).isRequired,
  billingCycle: PropTypes.oneOf(["monthly", "annual"]).isRequired,
  isCurrent: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onSelect: PropTypes.func,
  actionLabel: PropTypes.string,
};
